'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { useMemo, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import Form from '@nineeng/forms';
import { GenericDetailPage, Tabs, Spinner, MissingParentRolesAlert } from '@repo/components';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { Type, Role, Requirement, FormsApiResponse, TaskTemplatesApiResponse } from '@repo/shared/domain';
import { EventEmitter, constructQuery, guardedClient as clientRequest } from '@repo/shared/helpers';
import { getGroupTypes } from 'src/api/admin/types';
import { getTypeForModel } from 'src/api/admin/types';
import { getForms } from 'src/api/admin/forms';
import { getTaskTemplates } from 'src/api/admin/taskTemplates';
import { getRequirements } from 'src/api/admin/types';
import { getAllRoles } from 'src/api/admin/roles';

export const Route = createFileRoute('/_admin/config/types/group/$typeId')({
	component: GroupTypeDetailPage,
});

// Tab Components
interface GeneralTabProps {
	data: Type;
	onChange: (data: Type) => void;
	forms: FormsApiResponse['data'];
	roles: Role[];
}

function GeneralTab({ data, onChange, forms, roles }: GeneralTabProps) {
	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Type Name',
						name: 'name',
						type: 'TextField',
						required: true,
					},
					{
						rowLabel: 'Internal Department',
						rowDescription: 'Enable this for internal department group types',
						name: 'isInternal',
						type: 'Checkbox',
					},
					{
						rowLabel: 'Form',
						rowDescription: 'Properties will be managed using the assigned form',
						name: 'form',
						type: 'AutoCompleteByID',
						options: forms.map((t) => ({ ...t, id: t.slug })),
						getOptionLabel: (o: any) => o.title,
					},
					{
						rowLabel: 'Mandatory Company Owner',
						rowDescription: 'Upon creation of a company, a company owner email must be added.',
						name: 'mandatoryGroupOwner',
						type: 'Checkbox',
					},
					{
						rowLabel: 'Admin Role',
						name: 'adminRoleId',
						type: 'AutoCompleteByID',
						options: roles,
						getOptionLabel: (o: any) => o.name,
					},
				].filter(Boolean),
			},
		],
	};

	return (
		<div>
			<Form schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />
		</div>
	);
}

interface RequirementsTabProps {
	data: Type;
	onChange: (data: Type) => void;
	taskTemplates: TaskTemplatesApiResponse['data'];
	requirements: Requirement[];
}

function RequirementsTab({ data, onChange, taskTemplates, requirements }: RequirementsTabProps) {
	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Tasks',
						rowDescription:
							'Selected tasks will automatically be assigned to this type. The tasks can be reordered by dragging them to another position.',
						name: 'tasks',
						type: 'AutoCompleteByID',
						options: taskTemplates.map((t) => ({ ...t, id: t.slug })),
						getOptionLabel: (o: any) => o.name,
						multiple: true,
						draggable: true,
					},
					{
						rowLabel: 'Requirements',
						rowDescription: 'For this type to be valid, the assigned requirements will be evaluated',
						name: 'requirements',
						type: 'AutoComplete',
						options: requirements.map((t) => ({ ...t, id: t.key })),
						getOptionLabel: (o: any) => o.name,
						multiple: true,
					},
				].filter(Boolean),
			},
		],
	};

	return (
		<div>
			<Form schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />
		</div>
	);
}

interface PermissionTabProps {
	data: {
		possibleRoleIds?: string[];
		lockDelegatedRoles?: boolean;
		assignableByRoleIds?: string[];
		[key: string]: any;
	};
	onChange: (_data: any) => void;
	roles: Role[];
	onValidationChange?: (_isValid: boolean) => void;
}

function PermissionTab({ data, onChange, roles, onValidationChange }: PermissionTabProps) {
	const [formKey, setFormKey] = useState(uuidv4());

	const missingParentRoles = useMemo(() => {
		if (!data.possibleRoleIds || data.possibleRoleIds.length === 0) return [];

		const selectedRoleIds = new Set(data.possibleRoleIds);
		const missing: Array<{ childRole: Role; parentRoles: Role[] }> = [];

		data.possibleRoleIds.forEach((roleId) => {
			const role = roles.find((r) => r.id === roleId);
			if (role && role.parentRoleIds && role.parentRoleIds.length > 0) {
				const hasParentSelected = role.parentRoleIds.some((parentId) => selectedRoleIds.has(parentId));
				if (!hasParentSelected) {
					const parentRoles = role.parentRoleIds
						.map((parentId) => roles.find((r) => r.id === parentId))
						.filter((r): r is Role => r !== undefined);
					missing.push({ childRole: role, parentRoles });
				}
			}
		});

		return missing;
	}, [data.possibleRoleIds, roles]);

	const hasValidRoleSelection = missingParentRoles.length === 0;

	useEffect(() => {
		onValidationChange?.(hasValidRoleSelection);
	}, [hasValidRoleSelection, onValidationChange]);

	const handleAddParentRole = (parentRoleId: string) => {
		const currentRoleIds = data.possibleRoleIds || [];
		if (!currentRoleIds.includes(parentRoleId)) {
			onChange({
				...data,
				possibleRoleIds: [...currentRoleIds, parentRoleId],
			});
			setFormKey(uuidv4());
		}
	};

	const form = {
		sections: [
			{
				type: 'SettingsSection',
				fields: [
					{
						rowLabel: 'Delegated Roles',
						rowDescription: 'These roles will be available to be assigned to users of this type',
						name: 'possibleRoleIds',
						type: 'AutoCompleteByID',
						options: roles,
						getOptionLabel: (o: any) => o.name,
						multiple: true,
					},
					missingParentRoles.length > 0 && {
						type: 'Custom',
						Component: () => (
							<MissingParentRolesAlert missingParentRoles={missingParentRoles} onAddParentRole={handleAddParentRole} />
						),
					},
					{
						rowLabel: 'Lock Delegated Roles',
						rowDescription: 'Disable the assignment of other roles on company-level',
						name: 'lockDelegatedRoles',
						type: 'Checkbox',
					},
					{
						rowLabel: 'Permitted Roles',
						rowDescription: 'Roles that will be allowed to use this type',
						name: 'assignableByRoleIds',
						type: 'AutoCompleteByID',
						options: roles,
						getOptionLabel: (o: any) => o.name,
						multiple: true,
					},
				].filter(Boolean),
			},
		],
	};

	return (
		<div>
			<Form key={formKey} schema={form} onChange={onChange} originalValues={data} CustomNavigation={() => null} />
		</div>
	);
}

function GroupTypeDetailPage() {
	const { typeId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [typeData, setTypeData] = useState<Type | null>(null);
	const [activeTab, setActiveTab] = useState(0);
	const [hasChanged, setChanged] = useState(false);
	const [isPermissionsValid, setIsPermissionsValid] = useState(true);
	const [isGeneralValid, setIsGeneralValid] = useState(true);

	useBreadcrumbLevel(0, 'Configuration', '/config/types/group');
	useBreadcrumbLevel(1, 'Company Types', '/config/types/group');
	useBreadcrumbLevel(2, typeData?.name || 'Company Type Details');

	const { data: type, isLoading: isTypeLoading } = useQuery({
		queryKey: ['type', 'group', typeId],
		queryFn: () => getTypeForModel('group', typeId),
	});

	const taskTemplatesQuery = constructQuery({ sort: 'name:asc', type: 'group,approval,contact', noPagination: 'true' });
	const { data: taskTemplates = { data: [] } } = useQuery({
		queryKey: ['task-templates-group'],
		queryFn: () => getTaskTemplates(taskTemplatesQuery),
	});

	const formQuery = constructQuery({ noPagination: 'true', type: 'group' });

	const { data: forms = { data: [] } } = useQuery({
		queryKey: ['forms-group'],
		queryFn: () => getForms(formQuery),
	});

	const { data: requirements = [] } = useQuery({
		queryKey: ['requirements'],
		queryFn: getRequirements,
	});

	const { data: rolesResponse } = useQuery({
		queryKey: ['roles-all'],
		queryFn: getAllRoles,
	});
	const roles = rolesResponse?.data ?? [];

	useEffect(() => {
		if (type && !typeData) {
			setTypeData(type);
		}
	}, [type, typeData]);

	const handleChange = (data: any) => {
		setChanged(true);
		setTypeData(data);
		setIsGeneralValid(!!data.name);
	};

	const saveChanges = async () => {
		if (!typeData) return;

		try {
			await clientRequest.put(
				`/api/types/group/${typeId}`,
				typeData.form === undefined
					? {
							...typeData,
							form: null,
						}
					: typeData,
			);
			EventEmitter.emit('toast', {
				type: 'success',
				message: 'Company Type updated!',
			});
			setChanged(false);
			queryClient.invalidateQueries({ queryKey: ['type', 'group', typeId] });
			queryClient.invalidateQueries({ queryKey: ['types-group'] });
		} catch (err: any) {
			EventEmitter.emit('toast', {
				type: 'error',
				message: err.status === 405 ? err.message : 'Failed to update Company Type.',
			});
		}
	};

	if (isTypeLoading || !typeData) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	const tabs = [
		{
			label: 'General',
			el: <GeneralTab data={typeData} onChange={handleChange} forms={forms.data} roles={roles} />,
		},
		{
			label: 'Requirements',
			el: (
				<RequirementsTab
					data={typeData}
					onChange={handleChange}
					taskTemplates={taskTemplates.data}
					requirements={requirements}
				/>
			),
		},
		{
			label: 'Permissions',
			el: (
				<PermissionTab
					data={typeData}
					onChange={handleChange}
					roles={roles}
					onValidationChange={setIsPermissionsValid}
				/>
			),
		},
	];

	return (
		<>
			<GenericDetailPage>
				<GenericDetailPage.Content className="bg-white">
					<Paper
						variant="outlined"
						sx={{
							display: 'flex',
							flexDirection: 'column',
							flexWrap: 'wrap',
							flexGrow: 1,
						}}
					>
						<Tabs
							value={activeTab}
							onChange={(i) => setActiveTab(i)}
							actions={
								<>
									<Button
										disabled={!hasChanged || !isPermissionsValid || !isGeneralValid}
										size="small"
										variant="outlined"
										onClick={saveChanges}
									>
										Save Changes
									</Button>
								</>
							}
						>
							<Tabs.TabList>
								{tabs.map((t, index) => (
									<Tabs.Tab key={t.label} value={index} label={t.label} />
								))}
							</Tabs.TabList>

							{tabs.map((tab, index) => (
								<Tabs.TabPanel selectedValue={activeTab} key={tab.label} value={index}>
									{typeData && tab.el}
								</Tabs.TabPanel>
							))}
						</Tabs>
					</Paper>
				</GenericDetailPage.Content>
			</GenericDetailPage>
		</>
	);
}
